<template>
  <ToastProvider swipe-direction="right">
    <ToastRoot
      v-for="item in toastState.items"
      :key="item.id"
      :open="item.open"
      :duration="item.duration"
      class="toast-root"
      :class="`toast-root--${item.type}`"
      @update:open="handleOpenChange(item.id, $event)"
    >
      <div class="toast-body">
        <span class="toast-icon">
          <IconCheckCircleFill v-if="item.type === 'success'" />
          <IconCloseCircleFill v-else-if="item.type === 'error'" />
          <IconExclamationCircleFill v-else-if="item.type === 'warning'" />
          <IconInfoCircleFill v-else-if="item.type === 'info'" />
          <IconLoading v-else-if="item.type === 'loading'" class="icon-spin" />
          <IconInfoCircleFill v-else />
        </span>
        <ToastDescription class="toast-description">
          {{ item.content }}
        </ToastDescription>
        <ToastClose class="toast-close" aria-label="Close">
          <IconClose />
        </ToastClose>
      </div>
    </ToastRoot>
    <ToastViewport class="toast-viewport" />
  </ToastProvider>
</template>

<script setup lang="ts">
  import {
    ToastProvider,
    ToastRoot,
    ToastDescription,
    ToastClose,
    ToastViewport,
  } from 'radix-vue';
  import {
    IconCheckCircleFill,
    IconCloseCircleFill,
    IconExclamationCircleFill,
    IconInfoCircleFill,
    IconLoading,
    IconClose,
  } from '@arco-design/web-vue/es/icon';
  import { toastState } from '@/utils/message';

  const CLOSE_ANIMATION_DURATION = 250;

  function handleOpenChange(id: string, open: boolean) {
    if (!open) {
      const item = toastState.items.find((i) => i.id === id);
      if (item) {
        item.open = false;
        item.onClose?.();
        setTimeout(() => {
          const index = toastState.items.findIndex((i) => i.id === id);
          if (index !== -1) toastState.items.splice(index, 1);
        }, CLOSE_ANIMATION_DURATION);
      }
    }
  }
</script>

<style lang="less">
  /* ToastViewport 经 Portal 渲染到 body，必须用全局（非 scoped）样式 */
  .toast-viewport {
    position: fixed;
    bottom: 20px;
    right: 20px;
    display: flex;
    flex-direction: column-reverse;
    gap: 8px;
    width: max-content;
    max-width: min(calc(100vw - 40px), 420px);
    z-index: 2147483647;
    list-style: none;
    margin: 0;
    padding: 0;
    outline: none;
  }

  .toast-root {
    display: flex;
    align-items: center;
    background: var(--color-bg-white, #fff);
    border-radius: var(--border-radius-medium, 8px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.06);
    padding: 12px 16px;
    min-width: 240px;
    pointer-events: auto;

    &[data-state='open'] {
      animation: toast-slide-in 0.22s cubic-bezier(0.16, 1, 0.3, 1);
    }

    &[data-state='closed'] {
      animation: toast-fade-out 0.25s ease forwards;
    }

    &[data-swipe='move'] {
      transform: translateX(var(--radix-toast-swipe-move-x));
    }

    &[data-swipe='cancel'] {
      transform: translateX(0);
      transition: transform 0.2s ease-out;
    }

    &[data-swipe='end'] {
      animation: toast-swipe-out 0.15s ease forwards;
    }
  }

  .toast-body {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  .toast-icon {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    font-size: 16px;
  }

  .toast-root--success .toast-icon {
    color: rgb(var(--green-6, 0, 180, 42));
  }

  .toast-root--error .toast-icon {
    color: rgb(var(--red-6, 245, 63, 63));
  }

  .toast-root--warning .toast-icon {
    color: rgb(var(--orange-6, 255, 125, 0));
  }

  .toast-root--info .toast-icon,
  .toast-root--loading .toast-icon {
    color: rgb(var(--blue-6, 22, 93, 255));
  }

  .toast-root--normal .toast-icon {
    color: var(--color-text-2, #4e5969);
  }

  .icon-spin {
    animation: icon-spin 1s linear infinite;
  }

  .toast-description {
    flex: 1;
    font-size: 14px;
    line-height: 1.5;
    color: var(--color-text-1, #1d2129);
    margin: 0;
  }

  .toast-close {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    margin-left: 4px;
    color: var(--color-text-3, #86909c);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    border-radius: 4px;
    font-size: 12px;
    transition: color 0.1s;

    &:hover {
      color: var(--color-text-1, #1d2129);
    }
  }

  @keyframes toast-slide-in {
    from {
      opacity: 0;
      transform: translateX(24px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes toast-fade-out {
    from {
      opacity: 1;
      transform: translateX(0);
    }
    to {
      opacity: 0;
      transform: translateX(12px);
    }
  }

  @keyframes toast-swipe-out {
    from {
      transform: translateX(var(--radix-toast-swipe-end-x));
    }
    to {
      transform: translateX(120%);
    }
  }

  @keyframes icon-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
